import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import CircularProgress from "@mui/material/CircularProgress";

import config from "../../../config";
import ScatterPlot from "../../shared/charts/ScatterPlot";
import { MeasurementType } from "../../shared/enums/Result";
import { useInternationalFormatters } from "../../shared/helpers/useInternationalFormatters";
import { ReportingMeasurement } from "../../shared/interfaces/ReportingMeasurement";
import { UTP2Data, UTP2Events } from "../../shared/interfaces/UTP2";
import { buildPhasePlots, buildPhaseRangeSeries, buildUltrasonicPhasePlots } from "./PhaseResolvedFunctions";
import { ReportingChartCheckbox } from "./ReportingChartCheckbox";

interface Props {
  loading?: boolean;
  data?: UTP2Events | UTP2Data;
  measurementType: MeasurementType;
  uhfBaseline?: number;
  reportingMeasurement?: ReportingMeasurement;
  isExpertOrGreater?: boolean;
  isEaEmployee?: boolean;
  onCheckboxChange?: (isChecked: boolean) => void;
  phaseShift?: number;
  riseTime?: [number, number];
  pulseWidth?: [number, number];
  reflow?: number;
  resultType?: string;
}

export function PhaseResolvedChart(props: Props) {
  const { t } = useTranslation();
  const { formatNumber } = useInternationalFormatters();

  const { reportingMeasurement } = props;

  const checkboxAvailable =
    config.reportsEnabled && props.isEaEmployee && props.isExpertOrGreater && props.onCheckboxChange;

  const [yAxisOptions, setYAxisOptions] = useState<Highcharts.YAxisOptions | undefined>();

  const xAxisTitle = t("units.degrees");
  const xAxisOptions: Highcharts.XAxisOptions = {
    tickInterval: 30,
    min: 0,
    max: 360,
  };

  useEffect(() => {
    switch (props.measurementType) {
      case MeasurementType.UHF:
        const baseline = props.uhfBaseline || -100;

        setYAxisOptions({
          min: baseline,
          max: baseline + 60,
          startOnTick: false,
          title: {
            text: t("units.dbm"),
          },
        });
        break;
      case MeasurementType.TEV:
        setYAxisOptions({
          min: 0,
          max: 60,
          title: {
            text: t("units.db"),
          },
        });
        break;
      case MeasurementType.Ultra:
        setYAxisOptions({
          min: -10,
          max: 68,
          startOnTick: false,
          title: {
            text: t("units.dbuv"),
          },
        });
        break;
      case MeasurementType.CablePD:
        //note: Cable PD and VPIS have dynamic Y axis min/max
        setYAxisOptions({
          title: {
            text: t("units.pc"),
          },
        });
        break;
      case MeasurementType.VPIS:
        //note: Cable PD and VPIS have dynamic Y axis min/max
        setYAxisOptions({
          title: {
            text: t("units.mV"),
          },
        });
        break;
      default:
        setYAxisOptions(undefined);
    }
  }, [props.measurementType, props.uhfBaseline, t]);

  const [sineWaves, setSineWaves] = useState<0 | 1 | 3>(props.measurementType === MeasurementType.VPIS ? 1 : 0);

  const phasePlotSeries = useMemo(() => {
    if (props.data && "headings" in props.data) {
      if (props.data.headings == null) {
        return undefined;
      }
      const plotValues = buildPhasePlots(props.data, props.measurementType, props.riseTime, props.pulseWidth);

      // now we have calculated the plot values we can set the yaxis correctly on dynamic types
      if (yAxisOptions?.max == null && yAxisOptions?.min == null) {
        const absMax = Math.max(...plotValues.map((o) => Math.abs(o.y)), 0);
        setYAxisOptions({
          ...yAxisOptions,
          max: absMax * 1.1,
          min: absMax * -1.1,
        });
      }
      return buildPhaseRangeSeries(plotValues, yAxisOptions, props.phaseShift, sineWaves, props.measurementType);
    } else if (props.data && props.measurementType === MeasurementType.Ultra) {
      // Ultrasonic data format for UTP2 v4.0 onwards has been changed - it now includes "data" and "headings" fields, similar to the phase data for other measurement types.
      // These will be calculated using the "buildPhaseRangeSeries" function above.
      // Pre v4.0 Ultrasonic measurements should continue to use the old "buildUltrasonicPhasePlots" function below.
      // See #12339 for more details.

      const plotValues = buildUltrasonicPhasePlots(props.data);
      return buildPhaseRangeSeries(plotValues, yAxisOptions, props.phaseShift, sineWaves, props.measurementType);
    } else return undefined;
  }, [props.data, props.measurementType, yAxisOptions, sineWaves, props.phaseShift, props.riseTime, props.pulseWidth]);

  const chartTooltipOptions: Highcharts.TooltipOptions = {
    formatter: function () {
      const formattedXValue = formatNumber(Number(this.x), 3);
      const formattedYValue = formatNumber(Number(this.y), 3);

      return `<span style="color:${this.color}">\u25CF</span> ${this.series.name}  <br /> x: <b>${formattedXValue}</b>  ${xAxisTitle} <br /> y: <b>${formattedYValue}</b> ${yAxisOptions?.title?.text}<br/>`;
    },
  };

  const handleCheckboxChange = (isChecked: boolean) => {
    if (props.onCheckboxChange) {
      props.onCheckboxChange(isChecked);
    }
  };

  if (props.loading) {
    return (
      <div className="d-flex justify-content-center">
        <CircularProgress size={65} />
      </div>
    );
  } else if (phasePlotSeries) {
    return (
      <>
        <ScatterPlot
          series={phasePlotSeries}
          yAxisOptions={yAxisOptions}
          xAxisTitle={xAxisTitle}
          xAxisOptions={xAxisOptions}
          enableLegend
          tooltipOptions={chartTooltipOptions}
          sineWaves={sineWaves}
          setSineWaves={setSineWaves}
          reflow={props.reflow}
        />
        {checkboxAvailable && (
          <>
            <ReportingChartCheckbox isChecked={reportingMeasurement !== undefined} onChange={handleCheckboxChange} />
          </>
        )}
      </>
    );
  } else {
    return null;
  }
}
